import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  type ComponentProps,
  type ReactNode,
} from 'react';
import './fonts-mono';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styles from './AdminShell.module.css';
import { PrefetchLink } from './components/PrefetchLink';
import {
  ADMIN_LOTS,
  ADMIN_PRODUCTS,
  INVENTAIRE_CLUSTER_LABEL,
  ADMIN_PROCUREMENT_QUOTES,
} from './constants/adminNavLabels';
import {
  PageShell,
  Nav,
  NavCluster,
  NavClusterIconLink,
  NavAccountMenu,
  NavUtilityCluster,
  NavHeaderDivider,
  Loader,
  Group,
  WP_ICON_NAV_MOBILE,
  Drawer,
  Stack,
  IconButton,
  useMediaQuery,
  useScrollbarReveal,
  ConfirmDeleteHost,
  Modal,
  ConfirmModalFooter,
  Text,
  Avatar,
  tokens,
  type NavClusterItem,
} from '@wapharma/design-system';
import { SessionExpiryWarning } from './components/SessionExpiryWarning';
import { NavBarSearch } from './components/NavBarSearch';
import { EditProfileModal } from './components/EditProfileModal';
import { SettingsOverlay } from './components/SettingsOverlay';
import { SetupWizard } from './components/SetupWizard';
import { LaunchWizard } from './components/LaunchWizard';
import mobileNavStyles from './components/adminMobileNav.module.css';
import a11yStyles from './adminShellA11y.module.css';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './contexts/ToastContext';
import { ADMIN_STAFF_ROLES } from './hooks/useStaffRoles';
import { ProductBulkImportProvider } from './contexts/ProductBulkImportContext';
import { GtinDumpSyncInboxProvider } from './contexts/GtinDumpSyncInboxContext';
import { INBOX_OPEN_EVENT } from './lib/inboxOpenEvent';
import { CatalogImageResolveInboxProvider } from './contexts/CatalogImageResolveInboxContext';
import { InboxCountProvider } from './contexts/InboxCountContext';
import { InboxListProvider } from './contexts/InboxListContext';
import { useInboxLivePoll } from './hooks/useInboxLivePoll';
import { NavCountsProvider } from './contexts/NavCountsContext';
import { useNavCounts, type NavCounts } from './contexts/NavCountsContext';
import type { AuthUser } from './contexts/AuthContext';

import { InboxNavFlyout } from './components/InboxNavFlyout';
import { MessagesNavLink } from './components/MessagesNavLink';
import { MessagesOpenRedirect } from './components/MessagesOpenRedirect';
import { ReportIssueWidget } from './components/ReportIssueWidget';
import { ScannerBridgeStatusChip } from './components/ScannerBridgeStatusChip';
import { usePosScannerBridge } from './hooks/usePosScannerBridge';
import { logAdminNavTransition } from './lib/adminNavDebug';
import { navTwotoneIcon, navTwotoneItemIcon } from './lib/mxTwotoneIcon';
import {
  BoxTwotone,
  Chart2Twotone,
  ClipboardTextTwotone,
  DocumentTextTwotone,
  HamburgerMenuTwotone,
  HomeTwotone,
  LifebuoyTwotone,
  Money4Twotone,
  PeopleTwotone,
  Receipt2Twotone,
  Setting2Twotone,
  ShopTwotone,
  ShoppingCartTwotone,
  TagTwotone,
  TargetTwotone,
  TruckTickTwotone,
  UserEditTwotone,
} from './icons/mxNavTwotone';

function ShellLink(props: ComponentProps<typeof Link>) {
  return <PrefetchLink {...props} />;
}

type ClusterDef = {
  key: string;
  label: string;
  triggerIcon: ReactNode;
  items: NavClusterItem[];
};

function buildClusters(user: AuthUser | null, counts: NavCounts): ClusterDef[] {
  if (!user) return [];

  const isSuper = user.roles.includes('SUPER_ADMIN');
  const showCatalogue = user.roles.some((r) => ['SUPER_ADMIN', 'CATALOG', 'INVENTORY', 'VIEWER', 'SALES'].includes(r));
  const canStockNav = user.roles.some((r) => ['SUPER_ADMIN', 'INVENTORY', 'VIEWER'].includes(r));
  const canCatalogProductsNav = user.roles.some((r) =>
    ['SUPER_ADMIN', 'CATALOG', 'SALES', 'VIEWER', 'INVENTORY'].includes(r),
  );
  const showProcurement = user.roles.some((r) =>
    ['SUPER_ADMIN', 'INVENTORY', 'VIEWER'].includes(r),
  );
  const showCustomer = user.roles.some((r) => ['SUPER_ADMIN', 'SALES', 'VIEWER'].includes(r));
  const showDocuments = user.roles.some((r) =>
    ['SUPER_ADMIN', 'SALES', 'VIEWER', 'INVENTORY'].includes(r),
  );

  const c = (n: number) => n || undefined;

  const out: ClusterDef[] = [];

  if (showCatalogue) {
    const inventaireItems: NavClusterItem[] = [];
    if (canCatalogProductsNav) {
      inventaireItems.push({
        to: ADMIN_PRODUCTS.path,
        label: ADMIN_PRODUCTS.label,
        icon: navTwotoneItemIcon(TagTwotone),
        count: c(counts.products),
      });
    }
    if (canStockNav) {
      inventaireItems.push({
        to: ADMIN_LOTS.path,
        label: ADMIN_LOTS.shortLabel,
        icon: navTwotoneItemIcon(BoxTwotone),
        count: undefined,
      });
    }
    if (inventaireItems.length > 0) {
      out.push({
        key: 'inventaire',
        label: INVENTAIRE_CLUSTER_LABEL,
        triggerIcon: navTwotoneIcon(BoxTwotone),
        items: inventaireItems,
      });
    }
  }

  const venteItems: NavClusterItem[] = [
    { to: '/orders', label: 'Commandes', icon: navTwotoneItemIcon(ShoppingCartTwotone), count: c(counts.orders) },
  ];
  if (isSuper || user.roles.includes('SALES')) {
    venteItems.push({
      to: '/pos/sessions',
      label: 'Caisses',
      icon: navTwotoneItemIcon(Money4Twotone),
    });
  }
  if (showCustomer) {
    venteItems.push({ to: '/customer', label: 'Clients', icon: navTwotoneItemIcon(PeopleTwotone), count: c(counts.customers) });
  }
  if (isSuper || user.roles.includes('SALES')) {
    venteItems.push({ to: '/leads', label: 'Leads', icon: navTwotoneItemIcon(TargetTwotone), count: c(counts.leads) });
  }
  out.push({
    key: 'vente',
    label: 'Vente',
    triggerIcon: navTwotoneIcon(Receipt2Twotone),
    items: venteItems,
  });

  const pilotageItems: NavClusterItem[] = [];
  if (showDocuments) {
    pilotageItems.push({ to: '/documents', label: 'Documents', icon: navTwotoneItemIcon(DocumentTextTwotone), count: c(counts.documents) });
  }
  // Comptabilité UI parked — re-enable with route in App.tsx when ready.
  // if (isSuper || user.roles.includes('ACCOUNTING')) {
  //   pilotageItems.push({
  //     to: '/accounting',
  //     label: 'Comptabilité',
  //     icon: navTwotoneItemIcon(Chart2Twotone),
  //   });
  // }
  if (pilotageItems.length > 0) {
    out.push({
      key: 'pilotage',
      label: 'Pilotage',
      triggerIcon: navTwotoneIcon(Chart2Twotone),
      items: pilotageItems,
    });
  }

  if (showProcurement) {
    out.push({
      key: 'achats',
      label: 'Achats',
      triggerIcon: navTwotoneIcon(TruckTickTwotone),
      items: [
        {
          to: '/procurement/purchase-orders',
          label: 'Bons de commande',
          icon: navTwotoneItemIcon(ClipboardTextTwotone),
          count: c(counts.purchaseOrders),
        },
        {
          to: '/procurement/suppliers',
          label: 'Fournisseurs',
          icon: navTwotoneItemIcon(ShopTwotone),
          count: c(counts.suppliers),
        },
        {
          to: ADMIN_PROCUREMENT_QUOTES.path,
          label: ADMIN_PROCUREMENT_QUOTES.label,
          icon: navTwotoneItemIcon(DocumentTextTwotone),
        },
      ],
    });
  }

  const canSupportNav = isSuper || user.roles.includes('SALES');
  const adminItems: NavClusterItem[] = [];
  if (canSupportNav) {
    adminItems.push({
      to: '/support',
      label: 'Support',
      icon: navTwotoneItemIcon(LifebuoyTwotone),
      count: c(counts.supportTickets),
    });
  }
  if (isSuper) {
    adminItems.push({
      to: '/users',
      label: 'Utilisateurs',
      icon: navTwotoneItemIcon(UserEditTwotone),
      count: c(counts.users),
    });
  }
  if (adminItems.length > 0) {
    out.push({
      key: 'admin',
      label: 'Administration',
      triggerIcon: navTwotoneIcon(Setting2Twotone),
      items: adminItems,
    });
  }

  return out;
}

export function AdminShell() {
  const { user, loading, logout, mustChangePassword } = useAuth();
  const { showToast } = useToast();
  const loc = useLocation();
  const sessionBlockedRef = useRef(false);

  useEffect(() => {
    if (loading || !user || mustChangePassword || sessionBlockedRef.current) return;
    const hasStaffRole = user.roles.some((r) =>
      (ADMIN_STAFF_ROLES as readonly string[]).includes(r),
    );
    if (!user.active || !hasStaffRole) {
      sessionBlockedRef.current = true;
      showToast(
        !user.active ? 'Compte désactivé.' : "Ce compte n'a pas accès à l'administration.",
        { tone: 'error' },
      );
      void logout();
    }
  }, [loading, logout, mustChangePassword, showToast, user]);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <Loader />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: loc }} replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/force-password-change" replace />;
  }

  return (
    <ProductBulkImportProvider>
      <CatalogImageResolveInboxProvider>
        <GtinDumpSyncInboxProvider>
          <InboxCountProvider>
            <InboxListProvider>
              <NavCountsProvider>
                <AdminShellFrame user={user} logout={logout} />
              </NavCountsProvider>
            </InboxListProvider>
          </InboxCountProvider>
        </GtinDumpSyncInboxProvider>
      </CatalogImageResolveInboxProvider>
    </ProductBulkImportProvider>
  );
}

function AdminShellFrame({
  user,
  logout,
}: {
  user: AuthUser;
  logout: () => Promise<void>;
}) {
  useInboxLivePoll();
  useScrollbarReveal();
  const navCounts = useNavCounts();
  const loc = useLocation();
  const navigate = useNavigate();
  const narrowNav = useMediaQuery(tokens.media.mdMax);
  const canWriteCatalog = user.roles.some((r) => r === 'SUPER_ADMIN' || r === 'CATALOG');
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scannerNowMs, setScannerNowMs] = useState(() => Date.now());

  // Deep-link to a notification (e.g. GTIN sync started) — drop overlays covering the flyout.
  useEffect(() => {
    const onInboxOpen = () => setSettingsOpen(false);
    window.addEventListener(INBOX_OPEN_EVENT, onInboxOpen);
    return () => window.removeEventListener(INBOX_OPEN_EVENT, onInboxOpen);
  }, []);

  const onScannerScan = useCallback(
    (code: string) => {
      if (!canWriteCatalog) return;
      const digits = code.replace(/\D/g, '') || code.trim();
      if (!digits) return;
      navigate(`${ADMIN_PRODUCTS.path}?create=1&gtin=${encodeURIComponent(digits)}`);
    },
    [canWriteCatalog, navigate],
  );

  const scannerBridge = usePosScannerBridge({
    enabled: canWriteCatalog,
    onScan: onScannerScan,
  });

  useEffect(() => {
    if (!canWriteCatalog) return;
    const id = window.setInterval(() => setScannerNowMs(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, [canWriteCatalog]);

  const clusters = useMemo(() => buildClusters(user, navCounts), [user, navCounts]);
  const prevPathRef = useRef(loc.pathname);
  const brandKey = 'hamssah';
  const accountLabel =
    [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ') || user.email;

  useEffect(() => {
    document.documentElement.dataset.brand = brandKey;
    return () => {
      delete document.documentElement.dataset.brand;
    };
  }, [brandKey]);

  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev !== loc.pathname) {
      logAdminNavTransition(prev, loc.pathname);
      prevPathRef.current = loc.pathname;
    }
    setMobileNavOpen(false);
  }, [loc.pathname]);

  /** `PageRouteScaffold` viewportLock can leave html/body overflow hidden — reset before paint on nav. */
  useLayoutEffect(() => {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    const scrollHost = document.querySelector<HTMLElement>('.outletFrame');
    if (scrollHost) scrollHost.scrollTop = 0;
    const main = document.getElementById('admin-main-content');
    if (main && typeof main.focus === 'function') {
      try {
        main.focus({ preventScroll: true });
      } catch {
        main.focus();
      }
    }
  }, [loc.pathname]);

  const showSearch = user.roles.some((r) =>
    ['SUPER_ADMIN', 'CATALOG', 'SALES', 'VIEWER'].includes(r),
  );

  const navActive = (path: string) => {
    if (path === '/') return loc.pathname === '/';
    if (path === '/users') {
      return (
        loc.pathname === '/users' ||
        loc.pathname.startsWith('/users/') ||
        loc.pathname === '/roles' ||
        loc.pathname.startsWith('/roles/')
      );
    }
    return loc.pathname === path || loc.pathname.startsWith(`${path}/`);
  };

  const mobileNavPanel = narrowNav ? (
    <Drawer placement="left" opened={mobileNavOpen} onClose={() => setMobileNavOpen(false)} title="Navigation">
      <Stack gap="lg">
        {showSearch ? (
          <div className={mobileNavStyles.searchWrap}>
            <NavBarSearch />
          </div>
        ) : null}
        <div className={mobileNavStyles.section}>
          <h2 className={mobileNavStyles.sectionLabel}>Accueil</h2>
          <Stack gap="xs">
            <ShellLink
              to="/"
              className={mobileNavStyles.link}
              data-active={navActive('/') ? 'true' : 'false'}
              aria-current={navActive('/') ? 'page' : undefined}
            >
              {navTwotoneIcon(HomeTwotone)}
              À traiter
            </ShellLink>
          </Stack>
        </div>
        {clusters.map((c) => (
          <div key={c.key} className={mobileNavStyles.section}>
            <h2 className={mobileNavStyles.sectionLabel}>{c.label}</h2>
            <Stack gap="xs">
              {c.items.map((it) => (
                <ShellLink
                  key={it.to}
                  to={it.to}
                  className={mobileNavStyles.link}
                  data-active={navActive(it.to) ? 'true' : 'false'}
                  aria-current={navActive(it.to) ? 'page' : undefined}
                >
                  {it.icon}
                  {it.label}
                </ShellLink>
              ))}
            </Stack>
          </div>
        ))}
      </Stack>
    </Drawer>
  ) : null;

  return (
    <>
      <a href="#admin-main-content" className={a11yStyles.skipLink}>
        Aller au contenu principal
      </a>
      <PageShell
        contentId="admin-main-content"
        header={
        <Nav
          brand={
            <>
              {narrowNav ? (
                <>
                  <IconButton
                    type="button"
                    variant="soft"
                    size="md"
                    aria-label="Ouvrir le menu de navigation"
                    aria-expanded={mobileNavOpen}
                    onClick={() => setMobileNavOpen(true)}
                  >
                    <HamburgerMenuTwotone size={WP_ICON_NAV_MOBILE.size} color="currentColor" aria-hidden />
                  </IconButton>
                  {mobileNavPanel}
                </>
              ) : null}
              <NavClusterIconLink
                to="/"
                label="À traiter"
                icon={navTwotoneIcon(HomeTwotone, narrowNav ? { size: WP_ICON_NAV_MOBILE.size } : undefined)}
                LinkComponent={ShellLink}
                active={loc.pathname === '/'}
              />
              {!narrowNav
                ? clusters.map((c) => (
                    <NavCluster
                      key={c.key}
                      label={c.label}
                      triggerIcon={c.triggerIcon}
                      items={c.items}
                      LinkComponent={ShellLink}
                      active={c.items.some((it) => navActive(it.to))}
                      isItemActive={(to) => navActive(to)}
                    />
                  ))
                : null}
            </>
          }
          end={
            <Group
              gap={narrowNav ? 'xs' : 'sm'}
              align="center"
              wrap="nowrap"
              className={styles.headerEnd}
            >
              {canWriteCatalog && !narrowNav ? (
                <ScannerBridgeStatusChip state={scannerBridge} nowMs={scannerNowMs} />
              ) : null}
              {narrowNav ? null : showSearch ? <NavBarSearch /> : null}
              <MessagesOpenRedirect />
              <NavUtilityCluster label="Boîte de réception et messages">
                <MessagesNavLink
                  LinkComponent={ShellLink}
                  active={loc.pathname === '/messages' || loc.pathname.startsWith('/messages/')}
                />
                <InboxNavFlyout />
              </NavUtilityCluster>
              {narrowNav ? null : <NavHeaderDivider />}
              <NavAccountMenu
                displayName={user.firstName?.trim() || accountLabel}
                identityAvatar={
                  <Avatar label={accountLabel} src={user.profilePictureUrl} size="md" />
                }
                triggerIcon={
                  <Avatar
                    label={accountLabel}
                    src={user.profilePictureUrl}
                    size="nav"
                  />
                }
                onLogout={() => setLogoutConfirmOpen(true)}
                extraItems={[
                  {
                    key: 'edit-profile',
                    label: 'Profil',
                    icon: navTwotoneItemIcon(UserEditTwotone),
                    onSelect: () => setEditProfileOpen(true),
                  },
                  {
                    key: 'settings',
                    label: 'Paramètres',
                    icon: navTwotoneItemIcon(Setting2Twotone),
                    onSelect: () => setSettingsOpen(true),
                  },
                ]}
              />
            </Group>
          }
        />
      }
    >
        <Suspense
          fallback={
            <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
              <Loader />
            </div>
          }
        >
          <div key={loc.pathname} className={`outletFrame ${styles.pageIn}`}>
            <Outlet />
          </div>
        </Suspense>
      </PageShell>
      <SessionExpiryWarning />
      <EditProfileModal opened={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      <SettingsOverlay
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onReportIssue={() => setReportIssueOpen(true)}
      />
      <SetupWizard />
      <LaunchWizard />
      <ReportIssueWidget
        hideTrigger
        defaultOpened={reportIssueOpen}
        onClosed={() => setReportIssueOpen(false)}
      />
      <Modal
        opened={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title="Se déconnecter ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Annuler"
            confirmLabel="Se déconnecter"
            onCancel={() => setLogoutConfirmOpen(false)}
            onConfirm={() => {
              setLogoutConfirmOpen(false);
              void logout();
            }}
          />
        }
      >
        <Text size="sm">Vous allez quitter votre session admin.</Text>
      </Modal>
      <ConfirmDeleteHost />
    </>
  );
}
